import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getDealerById } from './dealerService';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Avatar, 
  Chip,
  Divider,
  Rating,
  Button,
  Tab,
  Tabs,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  CircularProgress,
  styled
} from '@mui/material';
import {
  LocationOn,
  Phone,
  Email,
  Star,
  Work,
  CalendarToday,
  CarRepair,
  ThumbUp,
  Comment,
  Business
} from '@mui/icons-material';

// Styled components (keep your existing styled components)

const DealerProfile = () => {
  const [tabValue, setTabValue] = useState(0);
  const [dealer, setDealer] = useState(null);
  const [loading, setLoading] = useState(true);
  const { dealerId } = useParams();

  useEffect(() => {
    const fetchDealer = async () => {
      try {
        setLoading(true);
        const dealerData = await getDealerById(dealerId);
        setDealer(dealerData);
      } catch (error) {
        console.error("Error fetching dealer:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDealer();
  }, [dealerId]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!dealer) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <Typography variant="h6">Dealer not found</Typography>
      </Box>
    );
  }
  // Styled components
const ProfileCard = styled(Card)({
    borderRadius: '16px',
    boxShadow: '0 10px 20px rgba(0,0,0,0.08)',
    overflow: 'hidden'
  });
  
  const ServiceCard = styled(Card)({
    borderLeft: '4px solid #3f51b5',
    transition: 'transform 0.3s',
    '&:hover': {
      transform: 'translateY(-5px)'
    }
  });

  return (
        <Box sx={{ p: 3 }}>
          <Typography variant="h4" gutterBottom sx={{ 
            fontWeight: 700,
            mb: 3,
            background: 'linear-gradient(90deg, #3f51b5 0%, #2196f3 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Dealer Profile
          </Typography>
    
          <Grid container spacing={3}>
            {/* Profile Column */}
            <Grid item xs={12} md={4}>
              <ProfileCard>
                <Box sx={{ 
                  height: '120px',
                  background: 'linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)'
                }} />
                <CardContent sx={{ position: 'relative' }}>
                  <Avatar sx={{ 
                    width: 100, 
                    height: 100,
                    position: 'absolute',
                    top: -50,
                    left: 20,
                    border: '4px solid white',
                    backgroundColor: 'white'
                  }}>
                    <Business sx={{ fontSize: 50, color: '#3f51b5' }} />
                  </Avatar>
                  
                  <Box sx={{ mt: 6 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      {dealer.name}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Rating 
                        value={dealer.rating} 
                        precision={0.1} 
                        readOnly 
                        size="small"
                      />
                      <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
                        {dealer.rating} ({dealer.reviewCount} reviews)
                      </Typography>
                    </Box>
                    
                    <Chip 
                      label={`${dealer.yearsActive} years in business`}
                      size="small"
                      icon={<CalendarToday fontSize="small" />}
                      sx={{ mt: 2 }}
                    />
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <LocationOn color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={dealer.location} />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <Phone color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={dealer.phone} />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <Email color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={dealer.email} />
                      </ListItem>
                    </List>
                    
                    <Button 
                      variant="contained" 
                      fullWidth 
                      sx={{ mt: 2, borderRadius: '8px' }}
                    >
                      Contact Dealer
                    </Button>
                  </Box>
                </CardContent>
              </ProfileCard>
              
              <Card sx={{ mt: 3, borderRadius: '16px', boxShadow: '0 5px 15px rgba(0,0,0,0.05)' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Key Statistics
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Total Sales
                      </Typography>
                      <Typography variant="h5">
                        {dealer.stats.totalSales.toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Monthly Avg.
                      </Typography>
                      <Typography variant="h5">
                        {dealer.stats.monthlyAverage}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                          Satisfaction
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={dealer.stats.customerSatisfaction} 
                          sx={{ 
                            flexGrow: 1,
                            height: 8,
                            borderRadius: 4
                          }} 
                        />
                        <Typography variant="body2" sx={{ ml: 1, minWidth: 40 }}>
                          {dealer.stats.customerSatisfaction}%
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Main Content Column */}
            <Grid item xs={12} md={8}>
              <Paper sx={{ mb: 3, borderRadius: '12px' }}>
                <Tabs 
                  value={tabValue} 
                  onChange={handleTabChange}
                  variant="fullWidth"
                >
                  <Tab label="Services" icon={<Work />} />
                  <Tab label="Reviews" icon={<Star />} />
                  <Tab label="Inventory" icon={<CarRepair />} />
                </Tabs>
              </Paper>
              
              {tabValue === 0 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Offered Services
                  </Typography>
                  <Grid container spacing={2}>
                    {dealer.services.map((service, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <ServiceCard>
                          <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ 
                              mr: 2,
                              bgcolor: 'primary.light',
                              color: 'primary.main'
                            }}>
                              {service.icon}
                            </Avatar>
                            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                              {service.name}
                            </Typography>
                          </CardContent>
                        </ServiceCard>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
              
              {tabValue === 1 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Customer Reviews
                  </Typography>
                  
                  {dealer.recentReviews.map((review, index) => (
                    <Card key={index} sx={{ mb: 2, borderRadius: '12px' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {review.user}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {review.date}
                          </Typography>
                        </Box>
                        <Rating value={review.rating} readOnly size="small" sx={{ my: 1 }} />
                        <Typography variant="body1">
                          {review.comment}
                        </Typography>
                        <Box sx={{ display: 'flex', mt: 2 }}>
                          <Button size="small" startIcon={<ThumbUp />}>
                            Helpful
                          </Button>
                          <Button size="small" startIcon={<Comment />} sx={{ ml: 1 }}>
                            Reply
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                  
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    sx={{ mt: 2, borderRadius: '8px' }}
                  >
                    See All Reviews
                  </Button>
                </Box>
              )}
              
              {tabValue === 2 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Current Inventory
                  </Typography>
                  <Card sx={{ p: 2, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                      Inventory management coming soon
                    </Typography>
                    <Button variant="contained" sx={{ mt: 2, borderRadius: '8px' }}>
                      View External Inventory
                    </Button>
                  </Card>
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
  );
};

export default DealerProfile;